import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  getProfile, getAdminUsers, updateAdminUser, deleteAdminUser,
  getDuplicateReferrals, resolveReferral,
  markCommissionPaid, getAdminStats,
  getAdminReferrals, editReferral, deleteReferral, getAdminServices, markCommissionUnpaid,
  getAdminEvents, createAdminEvent, updateAdminEvent, getAdminEventAttendance, adminRemoveAttendance,
  setAdminAttendanceStatus, getAdminAttendanceHistory, getAdminAttendanceHistoryDetail,
  getEventTypes, createAdminEventType, updateAdminEventType,
  getAdminInvitados, updateAdminInvitado, convertInvitadoToMember, confirmInvitadoAttendance, getAuditLog,
  bulkTempPasswords
} from '../api/api';
import {
  StatTile, StatusBarChart, TrendLine, RankList, StatusBadge, STATUS_META, statusLabel, ReferralDetailModal,
  SkeletonPage, Pagination, paginate, folio, ExportCsvButton, SortTh, GroupTh, sortRows, Avatar,
  EVENT_TYPE_LABELS, EVENT_MODALITY_LABELS, formatEventDate, mergeEventTypeLabels,
  USER_STATUS_LABELS, USER_ROLE_LABELS
} from '../components/Charts';
import { uiAlert, uiConfirm, uiPrompt, uiMoneyPrompt, uiForm } from '../components/Modal';
import { uiToastSuccess, uiToastError } from '../components/Toast';

function StatusPill({ children, tone = 'neutral' }) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

const money = (n) => `$${parseFloat(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Limpia comas y signos de $ antes de convertir a número (evita el bug de "26,000" -> 26)
const parseMoney = (val) => parseFloat(String(val).replace(/,/g, '').replace(/\$/g, '').trim());

const STATUS_OPTIONS = Object.keys(STATUS_META).map((value) => ({ value, label: statusLabel(value) }));
const PAGE_SIZE = 15;

// La navegación de Admin ahora vive en el submenú del sidebar (/admin/<section>), ya no en
// pestañas horizontales aquí. Duplicados se fusionó como filtro rápido dentro de Referencias
// en vez de ser sección propia — ver referralView más abajo (la vista "Disputas" que existía
// aquí se quitó por completo a petición de Jesús Guevara). "Comisiones" se
// fusionó dentro de Referencias (cada fila ya trae su comisión), pero se deja el alias por si
// alguien tiene guardado el link viejo /admin/comisiones.
const SECTION_TO_TAB = {
  resumen: 'resumen', usuarios: 'users',
  comisiones: 'referrals', // alias viejo — primero, para que no gane sobre "referencias" abajo
  referencias: 'referrals', eventos: 'events', asistencia: 'attendance', invitados: 'invitados',
  auditoria: 'audit',
};

const AUDIT_ACTION_LABELS = {
  update_user: 'Cambio de usuario',
  resolve_referral: 'Referencia resuelta',
  mark_commission_paid: 'Comisión marcada pagada',
  mark_commission_unpaid: 'Comisión marcada no pagada',
  override_attendance: 'Asistencia corregida',
  convert_invitado: 'Invitado convertido a miembro',
  update_service_commission_base: 'Base de comisión cambiada',
  update_dues_paid_until: 'Anualidad actualizada',
  confirm_invitado_attendance: 'Asistencia de invitado confirmada',
};
const TAB_TO_SECTION = Object.fromEntries(Object.entries(SECTION_TO_TAB).map(([k, v]) => [v, k]));

const INVITADO_STATUS = {
  pendiente: { label: 'Pendiente', tone: 'warning' },
  aprobado: { label: 'Aprobado', tone: 'success' },
  rechazado: { label: 'Rechazado', tone: 'danger' },
  listo_entrevista: { label: 'Listo para entrevista', tone: 'info' },
  convertido: { label: 'Convertido a miembro', tone: 'success' },
};

// Pendiente o rechazado = solicitud de alta (se aprueba/rechaza). Activo o inactivo = ya era
// miembro (se desactiva/reactiva).
const isApplicant = (u) => u.status === 'pending' || u.status === 'rejected';

function AdminUsers() {
  const navigate = useNavigate();
  const location = useLocation();
  const { section } = useParams();
  const tab = SECTION_TO_TAB[section] || 'resumen';
  const goTab = (t) => navigate(`/admin/${TAB_TO_SECTION[t] || 'resumen'}`);
  const [profile, setProfile] = useState(null);
  const [referralView, setReferralView] = useState('all'); // 'all' | 'duplicates'
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  // allReferrals ya trae la comisión de cada una fusionada (member_commission, hub_commission,
  // paid, payment_date, commission_id) — Referencias y Comisiones son una sola pantalla ahora.
  const [allReferrals, setAllReferrals] = useState([]);
  const [services, setServices] = useState([]);
  const [referralSearch, setReferralSearch] = useState('');
  const [referralStatusFilter, setReferralStatusFilter] = useState(null);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [referralsPage, setReferralsPage] = useState(1);
  const [referralSort, setReferralSort] = useState({ key: null, dir: 'asc' });
  const [referralGroupBy, setReferralGroupBy] = useState(null); // null | 'referrer_name' | 'provider_business_name'
  const [adminEvents, setAdminEvents] = useState([]);
  const [invitadosList, setInvitadosList] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  // Rol elegido por owner al aprobar cada cuenta pendiente (solo owner ve este selector).
  const [approveRoleChoice, setApproveRoleChoice] = useState({});
  const [attendanceModal, setAttendanceModal] = useState(null); // { event, rows }
  const [checkinSelect, setCheckinSelect] = useState('');
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [attendanceDetailModal, setAttendanceDetailModal] = useState(null); // { user, rows }
  const [eventTypes, setEventTypes] = useState([]);
  const [eventTypesModalOpen, setEventTypesModalOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Resultado del último "generar contraseñas temporales" — se muestra en pantalla (y se
  // puede exportar a CSV) para repartirlas en la junta. No se guarda en ningún lado más:
  // si recargas la página, se pierde y hay que generarlas de nuevo si hace falta.
  const [tempPasswordResults, setTempPasswordResults] = useState(null);
  const [generatingPasswords, setGeneratingPasswords] = useState(false);

  const toggleSort = (setSort) => (key) => {
    setSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getProfile()
      .then(res => {
        if (!['admin', 'owner'].includes(res.data.role)) {
          navigate('/dashboard');
          return;
        }
        setProfile(res.data);
        loadAll();
      })
      .catch(() => navigate('/'));
  }, [navigate]);

  const loadAll = () => {
    getAdminStats(dateFrom, dateTo).then(res => setStats(res.data)).catch(() => {});
    getAdminUsers().then(res => setUsers(res.data)).catch(() => {});
    getDuplicateReferrals().then(res => setDuplicates(res.data)).catch(() => {});
    getAdminReferrals().then(res => setAllReferrals(res.data)).finally(() => setLoading(false));
    getAdminServices().then(res => setServices(res.data)).catch(() => {});
    getAdminEvents().then(res => setAdminEvents(res.data)).catch(() => {});
    getEventTypes().then(res => { setEventTypes(res.data); mergeEventTypeLabels(res.data); }).catch(() => {});
    getAdminAttendanceHistory().then(res => setAttendanceHistory(res.data)).catch(() => {});
    getAdminInvitados().then(res => setInvitadosList(res.data)).catch(() => {});
    getAuditLog().then(res => setAuditLog(res.data)).catch(() => {});
  };

  // El Resumen se recarga cuando cambia el rango de fechas custom
  useEffect(() => {
    if (dateFrom && dateTo) getAdminStats(dateFrom, dateTo).then(res => setStats(res.data)).catch(() => {});
  }, [dateFrom, dateTo]);

  // Polling — refresca todo el panel cada 25s sin que admin tenga que darle F5 para ver
  // usuarios/referencias/comisiones nuevas. Usa un ref para no quedarse con un loadAll
  // "viejo" (con dateFrom/dateTo desactualizados) atrapado en el intervalo.
  const loadAllRef = useRef(loadAll);
  loadAllRef.current = loadAll;
  useEffect(() => {
    if (!profile) return;
    const interval = setInterval(() => loadAllRef.current(), 25000);
    return () => clearInterval(interval);
  }, [profile]);

  const newEvent = async () => {
    const activeTypes = eventTypes.filter(t => t.active);
    if (activeTypes.length === 0) { await uiAlert('Primero crea al menos un tipo de evento activo ("+ Nuevo tipo de evento").'); return; }
    const result = await uiForm('Nueva fecha de evento', [
      {
        name: 'event_type', label: 'Evento', type: 'select', value: activeTypes[0].key,
        options: activeTypes.map(t => {
          const mods = [t.allows_fisico && 'presencial', t.allows_virtual && 'virtual'].filter(Boolean).join('/');
          return { value: t.key, label: `${t.label} (${mods})` };
        }),
      },
      // La modalidad se valida contra el tipo elegido al guardar — si no coincide, el error
      // te dice exactamente qué modalidad admite ese tipo.
      { name: 'modality', label: 'Modalidad', type: 'select', value: 'fisico', options: [{ value: 'fisico', label: 'Presencial' }, { value: 'virtual', label: 'Virtual' }] },
      { name: 'event_date', label: 'Fecha', type: 'date', value: '' },
      { name: 'event_time', label: 'Hora', type: 'time', value: '' },
      { name: 'location_or_link', label: 'Ubicación o liga', type: 'text', value: '' },
    ]);
    if (!result) return;
    if (!result.event_date || !result.event_time) { await uiAlert('Fecha y hora son requeridas.'); return; }
    try {
      await createAdminEvent(result);
      uiToastSuccess('Evento creado.');
      loadAll();
    } catch (err) {
      uiToastError('Error creando el evento');
    }
  };

  const YES_NO = [{ value: 'true', label: 'Sí' }, { value: 'false', label: 'No' }];

  // Alta de un tipo de evento nuevo (ej. "Sesión virtual", "Tarde de networking") — queda
  // disponible de inmediato en el selector de "Nueva fecha" de arriba, sin tocar código.
  // Define de una vez qué modalidades acepta y si admite invitados, para que esas reglas
  // no queden libres/implícitas como antes.
  const addEventType = async () => {
    const result = await uiForm('Nuevo tipo de evento', [
      { name: 'label', label: 'Nombre', type: 'text', value: '' },
      { name: 'allows_fisico', label: '¿Se puede hacer de forma presencial?', type: 'select', value: 'true', options: YES_NO },
      { name: 'allows_virtual', label: '¿Se puede hacer de forma virtual?', type: 'select', value: 'true', options: YES_NO },
      { name: 'allows_invitados', label: '¿Los miembros pueden llevar invitados a este evento?', type: 'select', value: 'true', options: YES_NO },
    ]);
    if (!result || !result.label || !result.label.trim()) return;
    if (result.allows_fisico === 'false' && result.allows_virtual === 'false') {
      await uiAlert('Debe permitir al menos una modalidad (presencial o virtual).');
      return;
    }
    try {
      await createAdminEventType({
        label: result.label.trim(),
        allows_fisico: result.allows_fisico === 'true',
        allows_virtual: result.allows_virtual === 'true',
        allows_invitados: result.allows_invitados === 'true',
      });
      uiToastSuccess('Tipo de evento creado.');
      loadAll();
    } catch (err) {
      uiToastError('Error creando el tipo de evento: ' + (err.response?.data?.error || err.message));
    }
  };

  const editEventType = async (t) => {
    const result = await uiForm('Editar tipo de evento', [
      { name: 'label', label: 'Nombre', type: 'text', value: t.label },
      { name: 'allows_fisico', label: '¿Se puede hacer de forma presencial?', type: 'select', value: String(t.allows_fisico), options: YES_NO },
      { name: 'allows_virtual', label: '¿Se puede hacer de forma virtual?', type: 'select', value: String(t.allows_virtual), options: YES_NO },
      { name: 'allows_invitados', label: '¿Los miembros pueden llevar invitados a este evento?', type: 'select', value: String(t.allows_invitados), options: YES_NO },
    ]);
    if (!result || !result.label || !result.label.trim()) return;
    if (result.allows_fisico === 'false' && result.allows_virtual === 'false') {
      await uiAlert('Debe permitir al menos una modalidad (presencial o virtual).');
      return;
    }
    try {
      await updateAdminEventType(t.key, {
        label: result.label.trim(),
        allows_fisico: result.allows_fisico === 'true',
        allows_virtual: result.allows_virtual === 'true',
        allows_invitados: result.allows_invitados === 'true',
      });
      uiToastSuccess('Tipo de evento actualizado.');
      loadAll();
    } catch (err) {
      uiToastError('Error actualizando el tipo de evento: ' + (err.response?.data?.error || err.message));
    }
  };

  // Desactivar (no borrar) para no romper eventos históricos que ya usan ese tipo — solo deja
  // de ofrecerse al crear una fecha nueva. Se puede reactivar después.
  const toggleEventTypeActive = async (t) => {
    const verb = t.active ? 'Desactivar' : 'Activar';
    if (!(await uiConfirm(`¿${verb} "${t.label}"?`))) return;
    try {
      await updateAdminEventType(t.key, { active: !t.active });
      uiToastSuccess(`Tipo de evento ${t.active ? 'desactivado' : 'activado'}.`);
      loadAll();
    } catch (err) {
      uiToastError('Error actualizando el tipo de evento');
    }
  };

  const editEvent = async (ev) => {
    const result = await uiForm(`Editar ${EVENT_TYPE_LABELS[ev.event_type]} — ${EVENT_MODALITY_LABELS[ev.modality]}`, [
      { name: 'event_date', label: 'Fecha', type: 'date', value: ev.event_date?.slice(0, 10) },
      { name: 'event_time', label: 'Hora', type: 'time', value: ev.event_time || '' },
      { name: 'location_or_link', label: 'Ubicación o liga', type: 'text', value: ev.location_or_link || '' },
    ]);
    if (!result) return;
    if (!result.event_date || !result.event_time) { await uiAlert('Fecha y hora son requeridas.'); return; }
    try {
      await updateAdminEvent(ev.id, result);
      uiToastSuccess('Evento actualizado.');
      loadAll();
    } catch (err) {
      uiToastError('Error actualizando el evento');
    }
  };

  const viewAttendance = async (ev) => {
    try {
      const res = await getAdminEventAttendance(ev.id);
      setCheckinSelect('');
      setAttendanceModal({ event: ev, rows: res.data });
    } catch (err) {
      uiToastError('Error cargando asistencia');
    }
  };

  const kickAttendee = async (userId, name) => {
    if (!(await uiConfirm(`¿Quitar a ${name} de la lista de asistentes?`))) return;
    try {
      await adminRemoveAttendance(attendanceModal.event.id, userId);
      uiToastSuccess('Asistente eliminado de la lista.');
      viewAttendance(attendanceModal.event);
      loadAll();
    } catch (err) {
      uiToastError('Error quitando al asistente');
    }
  };

  // Corrige a un asistente ya registrado a "ausente" (se equivocó al checar, o se fue antes) —
  // a diferencia de "Quitar", deja constancia en el histórico de inasistencia en vez de borrar.
  const markAbsentFromModal = async (userId, name) => {
    if (!(await uiConfirm(`¿Marcar a ${name} como ausente en este evento?`))) return;
    try {
      await setAdminAttendanceStatus(attendanceModal.event.id, userId, 'ausente');
      uiToastSuccess('Marcado como ausente.');
      viewAttendance(attendanceModal.event);
      loadAll();
    } catch (err) {
      uiToastError('Error marcando ausente');
    }
  };

  // Check-in manual el día del evento — para cuando Barbara registra en el momento a quien
  // llega, en vez de esperar a que el miembro se autoregistre desde su celular.
  const checkinAttendee = async () => {
    if (!checkinSelect) return;
    try {
      await setAdminAttendanceStatus(attendanceModal.event.id, checkinSelect, 'presente');
      uiToastSuccess('Asistencia registrada.');
      setCheckinSelect('');
      viewAttendance(attendanceModal.event);
      loadAll();
    } catch (err) {
      uiToastError('Error registrando la asistencia');
    }
  };

  const reviewInvitado = async (id, status) => {
    try {
      await updateAdminInvitado(id, status);
      uiToastSuccess(status === 'aprobado' ? 'Invitado aprobado.' : 'Invitado rechazado.');
      loadAll();
    } catch (err) {
      uiToastError('Error actualizando el invitado');
    }
  };

  const markReadyForInterview = async (i) => {
    if (!(await uiConfirm(`¿Marcar a ${i.name} como listo para entrevista?`))) return;
    try {
      await updateAdminInvitado(i.id, 'listo_entrevista');
      uiToastSuccess('Invitado marcado listo para entrevista.');
      loadAll();
    } catch (err) {
      uiToastError('Error actualizando el invitado');
    }
  };

  const convertToMember = async (i) => {
    if (!(await uiConfirm(`¿Convertir a ${i.name} en cuenta de miembro? Ya debió pasar la entrevista.`))) return;
    try {
      const res = await convertInvitadoToMember(i.id);
      await uiAlert(
        `Cuenta creada para ${res.data.user.name}.\n\nCorreo: ${res.data.user.email}\nContraseña temporal: ${res.data.temp_password}\n\nCompártesela por fuera de la plataforma y pídele que la cambie al entrar.`
      );
      loadAll();
    } catch (err) {
      uiToastError('Error convirtiendo el invitado: ' + (err.response?.data?.error || err.message));
    }
  };

  // Límite de 2 eventos por invitado (acordado con Jesús Guevara) — ya no admite excepción:
  // al llegar a 2/2 no se puede confirmar una 3ra asistencia desde aquí. El servidor aplica
  // el mismo límite del lado suyo (ver POST /confirm-attendance), esto es solo para no ni
  // siquiera mandar la petición y mostrar el aviso de una vez. attendance_count ya viene
  // deduplicado por teléfono/correo desde el servidor (misma persona, la haya llevado quien sea).
  const confirmAttendance = async (i) => {
    if (i.attendance_count >= 2) {
      await uiAlert(`${i.name} ya tiene ${i.attendance_count}/2 asistencias registradas. Es necesario pagar anualidad para seguir asistiendo.`);
      return;
    }
    try {
      await confirmInvitadoAttendance(i.id);
      uiToastSuccess('Asistencia confirmada.');
      loadAll();
    } catch (err) {
      uiToastError('Error confirmando asistencia: ' + (err.response?.data?.error || err.message));
    }
  };

  const openAttendanceDetail = async (u) => {
    try {
      const res = await getAdminAttendanceHistoryDetail(u.user_id);
      setAttendanceDetailModal({ user: u, rows: res.data });
    } catch (err) {
      uiToastError('Error cargando el histórico de asistencia');
    }
  };

  const toggleAttendanceStatus = async (eventId, newStatus) => {
    try {
      await setAdminAttendanceStatus(eventId, attendanceDetailModal.user.user_id, newStatus);
      uiToastSuccess(newStatus === 'presente' ? 'Marcado como presente.' : 'Marcado como ausente.');
      const res = await getAdminAttendanceHistoryDetail(attendanceDetailModal.user.user_id);
      setAttendanceDetailModal(m => ({ ...m, rows: res.data }));
      loadAll();
    } catch (err) {
      uiToastError('Error corrigiendo la asistencia');
    }
  };

  // Si la referencia que está abierta en el detalle se actualiza (por edición, resolución, etc.),
  // refresca lo que se ve en la ventana sin que el usuario tenga que cerrarla y volver a abrirla.
  useEffect(() => {
    if (!selectedReferral) return;
    const fresh = allReferrals.find(r => r.id === selectedReferral.id);
    if (fresh) setSelectedReferral(fresh);
  }, [allReferrals]); // eslint-disable-line react-hooks/exhaustive-deps

  // Si se llegó desde una notificación, abre directo el detalle de esa referencia (y se va a la
  // pestaña de Referencias para dar contexto, en vez de dejarlo parado en Resumen).
  useEffect(() => {
    const targetId = location.state?.openReferralId;
    if (!targetId || allReferrals.length === 0) return;
    const found = allReferrals.find(r => r.id === targetId);
    if (found) {
      setSelectedReferral(found);
      setReferralView('all');
      goTab('referrals');
    }
  }, [location.state, allReferrals]); // eslint-disable-line react-hooks/exhaustive-deps

  // La página se reinicia cada vez que cambia el filtro o la búsqueda — si no, podrías
  // quedarte parado en una "página 3" que ya no existe con la lista filtrada.
  useEffect(() => { setReferralsPage(1); }, [referralStatusFilter, referralSearch]);

  // Genera contraseña temporal para TODOS los miembros activos de un jalón — pensado para
  // dar acceso antes de una presentación en persona. Cada quien la usa una sola vez: en
  // cuanto entra, el sitio lo obliga a poner su propia contraseña (ver CambiarPassword.js).
  const generateAllTempPasswords = async () => {
    const activeMembers = users.filter(u => u.role === 'member' && u.status === 'active');
    if (activeMembers.length === 0) { await uiAlert('No hay miembros activos.'); return; }
    if (!(await uiConfirm(`¿Generar contraseña temporal para los ${activeMembers.length} miembros activos? Esto invalida su contraseña actual (si ya tenían una) — solo hazlo si vas a repartirles la nueva enseguida.`))) return;
    setGeneratingPasswords(true);
    try {
      const res = await bulkTempPasswords();
      setTempPasswordResults(res.data);
      uiToastSuccess(`${res.data.length} contraseñas generadas.`);
    } catch (err) {
      uiToastError('No se pudieron generar las contraseñas');
    }
    setGeneratingPasswords(false);
  };

  // Borra la cuenta y TODO su rastro (referencias, comisiones, servicios, invitados) — es
  // irreversible, así que en vez de un simple "¿estás seguro?" se pide escribir el nombre
  // exacto de la cuenta. Solo owner puede verlo (ver gate en el render) y el backend igual
  // lo re-valida (requireOwner + nunca sobre un owner ni sobre uno mismo).
  const deleteUserAccount = async (u) => {
    const typed = await uiPrompt(
      `Esto borra PERMANENTEMENTE la cuenta de "${u.name}" y todo su historial: referencias, comisiones, servicios, invitados que trajo. No se puede deshacer.\n\nEscribe su nombre exacto para confirmar:`
    );
    if (typed === null) return;
    if (typed.trim() !== u.name) {
      await uiAlert('El nombre no coincide exactamente. No se eliminó nada.');
      return;
    }
    try {
      await deleteAdminUser(u.id);
      uiToastSuccess('Cuenta eliminada.');
      loadAll();
    } catch (err) {
      uiToastError(err.response?.data?.error || 'No se pudo eliminar la cuenta');
    }
  };

  const updateUser = async (id, changes, confirmMessage) => {
    if (confirmMessage && !(await uiConfirm(confirmMessage))) return;
    try {
      await updateAdminUser(id, changes);
      uiToastSuccess('Usuario actualizado.');
      loadAll();
    } catch (err) {
      uiToastError(err.response?.data?.error || 'Error actualizando usuario');
    }
  };

  const ROLE_LABELS = { member: 'Miembro (negocio)', admin: 'Administrador', owner: 'Dueño' };

  const approveWithRole = async (u) => {
    const role = approveRoleChoice[u.id] || 'member';
    const label = ROLE_LABELS[role];
    if (!(await uiConfirm(`¿Aprobar a ${u.name} como ${label}?`))) return;
    try {
      await updateAdminUser(u.id, { status: 'active', role });
      uiToastSuccess('Usuario aprobado.');
      loadAll();
    } catch (err) {
      uiToastError('Error aprobando usuario');
    }
  };

  const resolve = async (id, status) => {
    let deal_amount = null;
    if (status === 'confirmed_deal') {
      const val = await uiMoneyPrompt('¿Monto final del trato (sin IVA)?');
      if (!val) return;
      deal_amount = parseMoney(val);
      if (isNaN(deal_amount) || deal_amount <= 0) {
        await uiAlert('Monto inválido. Escribe solo números, por ejemplo: 26000');
        return;
      }
    }
    try {
      await resolveReferral(id, { status, deal_amount });
      uiToastSuccess('Referencia resuelta.');
      loadAll();
    } catch (err) {
      uiToastError('Error resolviendo referencia');
    }
  };

  const editRef = async (r) => {
    const serviceOptions = services.map(s => ({ value: String(s.id), label: `${s.business_name} — ${s.service_name}` }));

    const fields = [
      { name: 'prospect_name', label: 'Nombre del prospecto', type: 'text', value: r.prospect_name },
      { name: 'prospect_phone', label: 'Teléfono del prospecto', type: 'text', value: r.prospect_phone || '' },
      { name: 'service_id', label: 'Empresa / Servicio', type: 'select', value: String(r.service_id), options: serviceOptions },
      { name: 'status', label: 'Estado', type: 'select', value: r.status, options: STATUS_OPTIONS },
      { name: 'deal_amount', label: 'Monto del trato sin IVA (déjalo igual si no aplica)', type: 'money', value: r.deal_amount || '' },
    ];

    const result = await uiForm(`Editar referencia ${folio(r.id)}`, fields);
    if (!result) return;

    let newAmount = null;
    if (result.deal_amount !== undefined && result.deal_amount !== '') {
      const parsed = parseMoney(result.deal_amount);
      if (isNaN(parsed) || parsed <= 0) {
        await uiAlert('Monto inválido. Escribe solo números, por ejemplo: 26000');
        return;
      }
      newAmount = parsed;
    }

    try {
      await editReferral(r.id, {
        prospect_name: result.prospect_name,
        prospect_phone: result.prospect_phone,
        deal_amount: newAmount,
        status: result.status !== r.status ? result.status : undefined,
        service_id: result.service_id !== String(r.service_id) ? parseInt(result.service_id, 10) : undefined,
      });
      uiToastSuccess('Referencia actualizada.');
      loadAll();
    } catch (err) {
      uiToastError('Error editando la referencia');
    }
  };

  const deleteRef = async (r) => {
    if (!(await uiConfirm(`¿Borrar la referencia ${folio(r.id)} (${r.prospect_name})? Esto no se puede deshacer.`))) return;
    try {
      await deleteReferral(r.id);
      uiToastSuccess('Referencia borrada.');
      loadAll();
    } catch (err) {
      uiToastError('Error borrando la referencia');
    }
  };

  const markPaid = async (id) => {
    try {
      await markCommissionPaid(id);
      uiToastSuccess('Comisión marcada como pagada.');
      loadAll();
    } catch (err) {
      uiToastError('Error marcando como pagada');
    }
  };

  const markUnpaid = async (commissionId, prospectName) => {
    if (!(await uiConfirm(`¿Marcar como NO pagada la comisión de ${prospectName}? Úsalo solo si se marcó pagada por error.`))) return;
    try {
      await markCommissionUnpaid(commissionId);
      uiToastSuccess('Comisión marcada como no pagada.');
      loadAll();
    } catch (err) {
      uiToastError('Error revirtiendo el pago');
    }
  };

  // Panel de detalle para administración: 100% de las decisiones posibles sobre una
  // referencia, en un solo lugar (mismo ReferralDetailModal que ve un miembro, pero con
  // esta barra de acciones extra que solo admin recibe). Reutiliza los handlers que ya
  // existen (editRef/resolve/markPaid/markUnpaid) — nada de endpoints nuevos.
  const adminReferralActions = (r) => {
    if (!r) return [];
    const actions = [
      { label: 'Editar', variant: 'secondary', onClick: () => { setSelectedReferral(null); editRef(r); } },
    ];
    if (r.status !== 'confirmed_deal' && r.status !== 'paid') {
      actions.push({ label: 'Confirmar: sí hubo negocio', variant: 'primary', onClick: () => { setSelectedReferral(null); resolve(r.id, 'confirmed_deal'); } });
    }
    if (r.status !== 'confirmed_no_deal') {
      actions.push({ label: 'Confirmar: no hubo negocio', variant: 'secondary', onClick: () => { setSelectedReferral(null); resolve(r.id, 'confirmed_no_deal'); } });
    }
    if (r.status !== 'rejected_duplicate') {
      actions.push({ label: 'Rechazar como duplicada', variant: 'secondary', onClick: () => { setSelectedReferral(null); resolve(r.id, 'rejected_duplicate'); } });
    }
    if (r.commission_id && !r.paid) {
      actions.push({ label: 'Marcar comisión pagada', variant: 'primary', onClick: () => { setSelectedReferral(null); markPaid(r.commission_id); } });
    }
    if (r.commission_id && r.paid) {
      actions.push({ label: 'Marcar comisión no pagada', variant: 'danger', onClick: () => { setSelectedReferral(null); markUnpaid(r.commission_id, r.prospect_name); } });
    }
    return actions;
  };

  if (loading || !profile) return <SkeletonPage />;

  return (
    <div className="page-wide">
      <h1 className="page-title">Panel de Administración</h1>
      <p className="page-subtitle">Usuarios, duplicados y comisiones de toda la red.</p>

      {error && <div className="msg-banner error">{error}</div>}

      {tab === 'resumen' && stats && (
        <>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '18px' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Desde</label>
              <input type="date" className="form-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Hasta</label>
              <input type="date" className="form-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            {(dateFrom || dateTo) && (
              <button className="btn btn-secondary btn-sm" onClick={() => { setDateFrom(''); setDateTo(''); getAdminStats().then(res => setStats(res.data)); }}>
                Quitar rango
              </button>
            )}
          </div>

          <div className="kpi-row">
            <StatTile label="Miembros activos" value={stats.members.active} />
            <StatTile label="Miembros pendientes" value={stats.members.pending} />
            <StatTile label="Comisiones por pagar" value={money(stats.commissions_pending)} />
            <StatTile label="Pagado este mes" value={money(stats.commissions_paid_this_month)} />
            <StatTile label="Invitados en el rango" value={stats.invitados_count} />
          </div>

          <StatusBarChart
            title="Referencias por estado"
            subtitle="Toda la red, todos los tiempos. Haz click en un estado para ver esas referencias."
            data={stats.referrals_by_status}
            onSelect={(status) => { setReferralStatusFilter(status); setReferralView('all'); goTab('referrals'); }}
          />

          <TrendLine
            title="Referencias creadas por semana"
            subtitle="Últimas 8 semanas"
            data={stats.referrals_weekly}
          />

          <div className="two-col-grid">
            <RankList title="Top referrers" subtitle="Miembros que más refieren" items={stats.top_referrers} nameKey="name" countKey="count" />
            <RankList title="Top empresas" subtitle="Miembros que más reciben referencias" items={stats.top_providers} nameKey="business_name" countKey="count" />
          </div>
        </>
      )}

      {tab === 'users' && (
        <div className="table-wrap">
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '10px' }}>
            <button onClick={generateAllTempPasswords} disabled={generatingPasswords} className="btn btn-secondary">
              {generatingPasswords ? 'Generando...' : 'Generar contraseñas temporales'}
            </button>
            <ExportCsvButton
              filename="usuarios.csv"
              rows={users}
              columns={[
                { key: 'name', label: 'Nombre' },
                { key: 'business_name', label: 'Negocio' },
                { key: 'email', label: 'Email' },
                { key: 'status', label: 'Estado' },
                { key: 'role', label: 'Rol' },
              ]}
            />
          </div>

          {tempPasswordResults && (
            <div className="card" style={{ marginBottom: '16px', border: '2px solid var(--brand-500)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <strong>Contraseñas temporales generadas — repártelas ahora, no se vuelven a mostrar</strong>
                <button onClick={() => setTempPasswordResults(null)} className="btn btn-secondary btn-sm">Cerrar</button>
              </div>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr><th>Nombre</th><th>Negocio</th><th>Correo</th><th>Contraseña temporal</th></tr>
                  </thead>
                  <tbody>
                    {tempPasswordResults.map(r => (
                      <tr key={r.id}>
                        <td>{r.name}</td>
                        <td>{r.business_name || '—'}</td>
                        <td>{r.email}</td>
                        <td><code>{r.temp_password}</code></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: '8px' }}>
                <ExportCsvButton
                  filename="contrasenas_temporales.csv"
                  rows={tempPasswordResults}
                  columns={[
                    { key: 'name', label: 'Nombre' },
                    { key: 'business_name', label: 'Negocio' },
                    { key: 'email', label: 'Correo' },
                    { key: 'temp_password', label: 'Contraseña temporal' },
                  ]}
                />
              </div>
            </div>
          )}

          <table className="data-table">
            <thead>
              <tr>
                <th>Nombre</th><th>Negocio</th><th>Email</th><th>Estado</th><th>Rol</th><th>Anualidad</th><th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>
                    <button className="link-button avatar-with-label" onClick={() => navigate(`/admin/miembros/${u.id}`)} title="Ver perfil completo">
                      <Avatar src={u.avatar_url} seed={u.name} size={26} />
                      {u.name}
                    </button>
                  </td>
                  <td>{u.business_name || '—'}</td>
                  <td>{u.email}</td>
                  <td><StatusPill tone={u.status === 'active' ? 'success' : u.status === 'rejected' ? 'danger' : u.status === 'inactive' ? 'neutral' : 'warning'}>{USER_STATUS_LABELS[u.status] || u.status}</StatusPill></td>
                  <td><StatusPill tone={u.role === 'owner' ? 'success' : u.role === 'admin' ? 'info' : 'neutral'}>{USER_ROLE_LABELS[u.role] || u.role}</StatusPill></td>
                  <td>
                    {u.dues_paid_until ? (
                      <StatusPill tone={new Date(u.dues_paid_until) >= new Date(new Date().toDateString()) ? 'success' : 'danger'}>
                        {new Date(u.dues_paid_until).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })}
                      </StatusPill>
                    ) : (
                      <StatusPill tone="neutral">Sin registrar</StatusPill>
                    )}
                  </td>
                  <td>
                    {u.role === 'owner' && profile.role !== 'owner' ? (
                      <span style={{ fontSize: '12px', color: 'var(--neutral-500)' }}>Solo un owner puede editar</span>
                    ) : (
                      <div className="admin-user-actions">
                        {/* Solicitudes (pendiente/rechazado): Aprobar o Rechazar. Miembros que ya
                            estaban (activo/inactivo): Desactivar o Reactivar, sin correo — a
                            petición de Bárbara, para no "rechazar" a alguien que ya era miembro. */}
                        {isApplicant(u) && profile.role === 'owner' && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <select
                              value={approveRoleChoice[u.id] || 'member'}
                              onChange={(e) => setApproveRoleChoice({ ...approveRoleChoice, [u.id]: e.target.value })}
                              className="form-input" style={{ padding: '4px 6px', fontSize: '12px', width: 'auto' }}
                            >
                              <option value="member">Miembro (negocio)</option>
                              <option value="admin">Administrador</option>
                              <option value="owner">Dueño</option>
                            </select>
                            <button onClick={() => approveWithRole(u)} className="btn btn-success btn-sm">Aprobar</button>
                          </span>
                        )}
                        {isApplicant(u) && profile.role !== 'owner' && (
                          <button onClick={() => updateUser(u.id, { status: 'active' })} className="btn btn-success btn-sm">Aprobar</button>
                        )}
                        {u.status === 'pending' && (
                          <button
                            onClick={() => updateUser(u.id, { status: 'rejected' }, `¿Rechazar a ${u.name}? No va a poder usar el sistema.`)}
                            className="btn btn-secondary btn-sm"
                          >
                            Rechazar
                          </button>
                        )}
                        {u.status === 'active' && u.id !== profile.id && (
                          <button
                            onClick={() => updateUser(u.id, { status: 'inactive' }, `¿Desactivar a ${u.name}? No va a poder iniciar sesión. Su historial de referencias y comisiones se conserva y puedes reactivarlo cuando quieras. No se le envía ningún correo.`)}
                            className="btn btn-secondary btn-sm"
                          >
                            Desactivar
                          </button>
                        )}
                        {u.status === 'inactive' && (
                          <button onClick={() => updateUser(u.id, { status: 'active' }, `¿Reactivar a ${u.name}? Va a poder iniciar sesión de nuevo.`)} className="btn btn-success btn-sm">
                            Reactivar
                          </button>
                        )}
                        {profile.role === 'owner' && u.id !== profile.id && u.role === 'member' && (
                          <>
                            <button
                              onClick={() => updateUser(u.id, { role: 'admin' }, `¿Dar permisos de administrador a ${u.name}? Va a poder ver y editar toda la información de la red.`)}
                              className="btn btn-secondary btn-sm"
                            >
                              Hacer admin
                            </button>
                            <button
                              onClick={() => updateUser(u.id, { role: 'owner' }, `¿Hacer a ${u.name} owner? Va a tener los mismos permisos que tú, incluyendo dar/quitar admins y editar otras cuentas owner.`)}
                              className="btn btn-secondary btn-sm"
                            >
                              Hacer owner
                            </button>
                          </>
                        )}
                        {profile.role === 'owner' && u.id !== profile.id && u.role === 'admin' && (
                          <>
                            <button
                              onClick={() => updateUser(u.id, { role: 'owner' }, `¿Hacer a ${u.name} owner? Va a tener los mismos permisos que tú.`)}
                              className="btn btn-secondary btn-sm"
                            >
                              Hacer owner
                            </button>
                            <button
                              onClick={() => updateUser(u.id, { role: 'member' }, `¿Quitar permisos de administrador a ${u.name}?`)}
                              className="btn btn-secondary btn-sm"
                            >
                              Quitar admin
                            </button>
                          </>
                        )}
                        {profile.role === 'owner' && u.id !== profile.id && u.role === 'owner' && (
                          <button
                            onClick={() => updateUser(u.id, { role: 'admin' }, `¿Quitarle el rol de owner a ${u.name}? Se queda como admin normal.`)}
                            className="btn btn-secondary btn-sm"
                          >
                            Quitar owner
                          </button>
                        )}
                        {u.id === profile.id && u.role === 'owner' && (
                          <span style={{ fontSize: '12px', color: 'var(--neutral-500)' }}>Eres tú</span>
                        )}
                        {profile.role === 'owner' && u.id !== profile.id && u.role !== 'owner' && (
                          <button
                            onClick={() => deleteUserAccount(u)}
                            className="btn btn-danger btn-sm"
                            title="Borra la cuenta y todo su historial — irreversible"
                          >
                            Eliminar
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && <div className="table-empty">Sin usuarios registrados</div>}
        </div>
      )}

      {tab === 'referrals' && (
        <div>
          <div className="tabs" style={{ marginBottom: '18px' }}>
            <button className={`tab ${referralView === 'all' ? 'active' : ''}`} onClick={() => setReferralView('all')}>Todas</button>
            <button className={`tab ${referralView === 'duplicates' ? 'active' : ''}`} onClick={() => setReferralView('duplicates')}>
              Duplicados {duplicates.length > 0 && <span className="badge badge-warning" style={{ marginLeft: '6px' }}>{duplicates.length}</span>}
            </button>
          </div>

          {referralView === 'duplicates' && (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr><th>Prospecto</th><th>Estado actual</th><th>Posible duplicado de</th><th>Acción</th></tr>
                </thead>
                <tbody>
                  {duplicates.map((r) => (
                    <tr key={r.id}>
                      <td>{r.prospect_name}</td>
                      <td><StatusBadge status={r.status} small /></td>
                      <td>#{r.possible_duplicate_of}</td>
                      <td><button onClick={() => resolve(r.id, 'rejected_duplicate')} className="btn btn-secondary btn-sm">Rechazar como duplicada</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {duplicates.length === 0 && <div className="table-empty">Sin duplicados por revisar</div>}
            </div>
          )}

          {referralView === 'all' && (
          <div>
          {referralStatusFilter && (
            <div className="filter-chip">
              Filtrando: {statusLabel(referralStatusFilter)}
              <button onClick={() => setReferralStatusFilter(null)} title="Quitar filtro">✕</button>
            </div>
          )}
          <input
            type="text"
            placeholder="Buscar por folio, prospecto, referrer o empresa..."
            value={referralSearch}
            onChange={(e) => setReferralSearch(e.target.value)}
            className="form-input"
            style={{ marginBottom: '16px', maxWidth: '420px' }}
          />
          {(() => {
            const filtered = sortRows(
              allReferrals
                .filter(r => !referralStatusFilter || r.status === referralStatusFilter)
                .filter(r => {
                  const q = referralSearch.trim().toLowerCase();
                  if (!q) return true;
                  return (
                    folio(r.id).toLowerCase().includes(q) ||
                    (r.prospect_name || '').toLowerCase().includes(q) ||
                    (r.referrer_name || '').toLowerCase().includes(q) ||
                    (r.provider_business_name || '').toLowerCase().includes(q)
                  );
                }),
              referralSort,
              (r, key) => key === 'status' ? statusLabel(r.status, r.provider_business_name) : r[key]
            );
            const referralRow = (r) => (
              <tr key={r.id}>
                <td>
                  <button className="link-button" onClick={() => setSelectedReferral(r)} title="Ver toda la información">
                    {folio(r.id)}
                  </button>
                </td>
                <td>{r.prospect_name} {r.prospect_phone ? `(${r.prospect_phone})` : ''}</td>
                <td><span className="avatar-with-label"><Avatar src={r.referrer_avatar_url} seed={r.referrer_name} size={22} />{r.referrer_name}</span></td>
                <td><span className="avatar-with-label"><Avatar src={r.provider_avatar_url} seed={r.provider_business_name} size={22} />{r.provider_business_name}</span></td>
                <td>{r.service_name}</td>
                <td className="mono-num">{r.deal_amount ? money(r.deal_amount) : '—'}</td>
                <td className="mono-num">{r.commission_id ? money(r.member_commission) : '—'}</td>
                <td className="mono-num">{r.commission_id ? money(r.hub_commission) : '—'}</td>
                <td>
                  {r.commission_id
                    ? <StatusPill tone={r.paid ? 'success' : 'warning'}>{r.paid ? 'Sí' : 'No'}</StatusPill>
                    : <span style={{ color: 'var(--neutral-500)' }}>—</span>}
                </td>
                <td><StatusBadge status={r.status} businessName={r.provider_business_name} small /></td>
                <td>
                  <div className="admin-user-actions">
                    <button onClick={() => editRef(r)} className="btn btn-secondary btn-sm">Editar</button>
                    {r.commission_id && !r.paid && (
                      <button onClick={() => markPaid(r.commission_id)} className="btn btn-primary btn-sm">Marcar pagada</button>
                    )}
                    {r.commission_id && r.paid && (
                      <button onClick={() => markUnpaid(r.commission_id, r.prospect_name)} className="btn btn-secondary btn-sm">Marcar no pagada</button>
                    )}
                    {profile.role === 'owner' && (
                      <button onClick={() => deleteRef(r)} className="btn btn-danger btn-sm">Borrar</button>
                    )}
                  </div>
                </td>
              </tr>
            );
            return (
              <>
                <div className="table-wrap">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                    {referralGroupBy && (
                      <div className="filter-chip">
                        Agrupado por: {referralGroupBy === 'referrer_name' ? 'Referrer' : 'Empresa'}
                        <button onClick={() => setReferralGroupBy(null)} title="Quitar agrupado">✕</button>
                      </div>
                    )}
                    <div style={{ marginLeft: 'auto' }}>
                      <ExportCsvButton
                        filename="referencias.csv"
                        rows={filtered}
                        columns={[
                          { key: (r) => folio(r.id), label: 'Folio' },
                          { key: 'prospect_name', label: 'Prospecto' },
                          { key: 'prospect_phone', label: 'Teléfono' },
                          { key: 'referrer_name', label: 'Referrer' },
                          { key: 'provider_business_name', label: 'Empresa' },
                          { key: 'service_name', label: 'Servicio' },
                          { key: (r) => r.deal_amount || '', label: 'Monto' },
                          { key: (r) => r.member_commission || '', label: 'Comisión referrer' },
                          { key: (r) => r.hub_commission || '', label: 'Comisión hub' },
                          { key: (r) => (r.commission_id ? (r.paid ? 'Sí' : 'No') : ''), label: 'Comisión pagada' },
                          { key: (r) => statusLabel(r.status, r.provider_business_name), label: 'Estado' },
                        ]}
                      />
                    </div>
                  </div>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Folio</th>
                        <SortTh label="Prospecto" sortKey="prospect_name" sort={referralSort} onSort={toggleSort(setReferralSort)} />
                        <GroupTh label="Referrer" groupKey="referrer_name" groupBy={referralGroupBy} onGroup={setReferralGroupBy} />
                        <GroupTh label="Empresa" groupKey="provider_business_name" groupBy={referralGroupBy} onGroup={setReferralGroupBy} />
                        <SortTh label="Servicio" sortKey="service_name" sort={referralSort} onSort={toggleSort(setReferralSort)} />
                        <SortTh label="Monto" sortKey="deal_amount" sort={referralSort} onSort={toggleSort(setReferralSort)} />
                        <th>Comisión referrer</th>
                        <th>Comisión hub</th>
                        <th>Pagada</th>
                        <SortTh label="Estado" sortKey="status" sort={referralSort} onSort={toggleSort(setReferralSort)} />
                        <th>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referralGroupBy ? (() => {
                        const groups = new Map();
                        filtered.forEach((r) => {
                          const gval = r[referralGroupBy] || 'Sin dato';
                          if (!groups.has(gval)) groups.set(gval, []);
                          groups.get(gval).push(r);
                        });
                        const groupNames = Array.from(groups.keys()).sort((a, b) => a.localeCompare(b, 'es'));
                        return groupNames.flatMap((gname) => [
                          <tr key={`group-${gname}`} className="table-group-row">
                            <td colSpan={11}>{gname} <span style={{ fontWeight: 400, color: 'var(--neutral-500)' }}>({groups.get(gname).length})</span></td>
                          </tr>,
                          ...groups.get(gname).map(referralRow),
                        ]);
                      })() : paginate(filtered, referralsPage, PAGE_SIZE).map(referralRow)}
                    </tbody>
                  </table>
                  {filtered.length === 0 && <div className="table-empty">Sin referencias que coincidan</div>}
                </div>
                {!referralGroupBy && (
                  <Pagination page={referralsPage} totalItems={filtered.length} pageSize={PAGE_SIZE} onPageChange={setReferralsPage} />
                )}
              </>
            );
          })()}
          </div>
          )}
        </div>
      )}

      <ReferralDetailModal referral={selectedReferral} onClose={() => setSelectedReferral(null)} actions={adminReferralActions(selectedReferral)} />

      {tab === 'events' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '10px' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setEventTypesModalOpen(true)}>Tipos de evento</button>
            <button className="btn btn-secondary btn-sm" onClick={addEventType}>+ Nuevo tipo de evento</button>
            <button className="btn btn-primary btn-sm" onClick={newEvent}>+ Nueva fecha</button>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr><th>Evento</th><th>Modalidad</th><th>Fecha</th><th>Hora</th><th>Ubicación / Liga</th><th>Asistentes</th><th>Acción</th></tr>
              </thead>
              <tbody>
                {adminEvents.map(ev => (
                  <tr key={ev.id}>
                    <td>{EVENT_TYPE_LABELS[ev.event_type] || ev.event_type}</td>
                    <td>{EVENT_MODALITY_LABELS[ev.modality] || ev.modality}</td>
                    <td style={{ textTransform: 'capitalize' }}>{formatEventDate(ev.event_date)}</td>
                    <td>{ev.event_time || '—'}</td>
                    <td>{ev.location_or_link || '—'}</td>
                    <td>
                      <button className="link-button" onClick={() => viewAttendance(ev)}>Ver lista</button>
                    </td>
                    <td><button onClick={() => editEvent(ev)} className="btn btn-secondary btn-sm">Editar</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {adminEvents.length === 0 && <div className="table-empty">Sin fechas registradas</div>}
          </div>
        </div>
      )}

      {tab === 'attendance' && (
        <div className="table-wrap">
          <p className="page-subtitle" style={{ marginTop: 0 }}>
            Solo fechas ya pasadas. "Faltas" cuenta tanto a quien nunca checó como a quien checó
            pero administración corrigió a ausente. Haz click en un miembro para ver el detalle y corregir una fecha puntual.
          </p>
          <table className="data-table">
            <thead>
              <tr><th>Miembro</th><th>Fechas pasadas</th><th>Asistencias</th><th>Faltas</th></tr>
            </thead>
            <tbody>
              {attendanceHistory.map(a => (
                <tr key={a.user_id}>
                  <td>
                    <button className="link-button avatar-with-label" onClick={() => openAttendanceDetail(a)}>
                      <Avatar src={a.avatar_url} seed={a.name} size={22} />{a.name}
                    </button>
                    {a.business_name ? ` — ${a.business_name}` : ''}
                  </td>
                  <td>{a.total_events}</td>
                  <td>{a.attended}</td>
                  <td>
                    <span className={`badge badge-${a.missed > 0 ? 'danger' : 'success'}`}>{a.missed}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {attendanceHistory.length === 0 && <div className="table-empty">Sin fechas de evento registradas todavía</div>}
        </div>
      )}

      {tab === 'invitados' && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr><th>Invitado</th><th>Negocio</th><th>Giro</th><th>Llevado por</th><th>Evento</th><th>Asistencias</th><th>Estado</th><th>Acción</th></tr>
            </thead>
            <tbody>
              {invitadosList.map(i => (
                <tr key={i.id}>
                  <td>{i.name}</td>
                  <td>{i.business_name || '—'}</td>
                  <td>{i.giro || '—'}</td>
                  <td><span className="avatar-with-label"><Avatar src={i.brought_by_avatar_url} seed={i.brought_by_name} size={22} />{i.brought_by_name}</span></td>
                  <td>{i.event_type ? `${EVENT_TYPE_LABELS[i.event_type]} — ${formatEventDate(i.event_date)}` : '—'}</td>
                  <td><span className={`badge badge-${i.attendance_count >= 2 ? 'danger' : 'neutral'}`}>{i.attendance_count}/2</span></td>
                  <td><StatusPill tone={INVITADO_STATUS[i.status]?.tone || 'neutral'}>{INVITADO_STATUS[i.status]?.label || i.status}</StatusPill></td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {i.status !== 'aprobado' && i.status !== 'listo_entrevista' && i.status !== 'convertido' && <button onClick={() => reviewInvitado(i.id, 'aprobado')} className="btn btn-success btn-sm">Aprobar</button>}
                      {i.status !== 'rechazado' && i.status !== 'convertido' && <button onClick={() => reviewInvitado(i.id, 'rechazado')} className="btn btn-secondary btn-sm">Rechazar</button>}
                      {i.status === 'aprobado' && <button onClick={() => markReadyForInterview(i)} className="btn btn-secondary btn-sm">Listo para entrevista</button>}
                      {i.status === 'listo_entrevista' && <button onClick={() => convertToMember(i)} className="btn btn-primary btn-sm">Convertir a miembro</button>}
                      {i.event_id && <button onClick={() => confirmAttendance(i)} className="btn btn-secondary btn-sm">Confirmar asistencia</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {invitadosList.length === 0 && <div className="table-empty">Sin invitados registrados</div>}
        </div>
      )}

      {tab === 'audit' && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr><th>Fecha</th><th>Quién</th><th>Acción</th><th>Detalle</th></tr>
            </thead>
            <tbody>
              {auditLog.map(a => (
                <tr key={a.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{new Date(a.created_at).toLocaleString('es-MX')}</td>
                  <td><span className="avatar-with-label"><Avatar src={a.actor_avatar_url} seed={a.actor_name} size={22} />{a.actor_name || 'Sistema'}</span></td>
                  <td>{AUDIT_ACTION_LABELS[a.action] || a.action}</td>
                  <td style={{ fontSize: '12px', color: 'var(--neutral-500)' }}>
                    {a.target_type} #{a.target_id}{a.details ? ` — ${JSON.stringify(a.details)}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {auditLog.length === 0 && <div className="table-empty">Sin actividad registrada</div>}
        </div>
      )}

      {attendanceModal && (
        <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setAttendanceModal(null); }}>
          <div className="modal-box detail-box" role="dialog" aria-modal="true">
            <div className="detail-header">
              <h3>Asistencia — {EVENT_TYPE_LABELS[attendanceModal.event.event_type]} ({formatEventDate(attendanceModal.event.event_date)})</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setAttendanceModal(null)}>Cerrar</button>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <select className="form-select" value={checkinSelect} onChange={(e) => setCheckinSelect(e.target.value)}>
                <option value="">Registrar asistencia de...</option>
                {users
                  .filter(u => u.status === 'active' && !attendanceModal.rows.some(r => String(r.user_id) === String(u.id)))
                  .map(u => <option key={u.id} value={u.id}>{u.name} — {u.business_name || '—'}</option>)}
              </select>
              <button className="btn btn-primary btn-sm" disabled={!checkinSelect} onClick={checkinAttendee}>Registrar</button>
            </div>

            {attendanceModal.rows.length === 0 ? <p>Nadie se ha registrado todavía.</p> : (
              <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px' }}>
                {attendanceModal.rows.map(r => (
                  <li key={r.id} style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                    <span className="avatar-with-label"><Avatar src={r.avatar_url} seed={r.name} size={20} />{r.name} — {r.business_name || '—'}</span>
                    <span>
                      <button className="btn btn-secondary btn-sm" onClick={() => markAbsentFromModal(r.user_id, r.name)} style={{ marginRight: '6px' }}>Marcar ausente</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => kickAttendee(r.user_id, r.name)}>Quitar</button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {eventTypesModalOpen && (
        <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setEventTypesModalOpen(false); }}>
          <div className="modal-box detail-box" role="dialog" aria-modal="true">
            <div className="detail-header">
              <h3>Tipos de evento</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setEventTypesModalOpen(false)}>Cerrar</button>
            </div>
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', fontSize: '13px' }}>
              {eventTypes.map(t => (
                <li key={t.key} style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid var(--neutral-200)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '4px' }}>
                    <strong>{t.label} {!t.active && <span className="badge badge-neutral">Inactivo</span>}</strong>
                    <span>
                      <button className="btn btn-secondary btn-sm" onClick={() => editEventType(t)} style={{ marginRight: '6px' }}>Editar</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => toggleEventTypeActive(t)}>{t.active ? 'Desactivar' : 'Activar'}</button>
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', color: 'var(--neutral-500)' }}>
                    <span className={`badge badge-${t.allows_fisico ? 'success' : 'neutral'}`}>{t.allows_fisico ? 'Presencial ✓' : 'Sin presencial'}</span>
                    <span className={`badge badge-${t.allows_virtual ? 'success' : 'neutral'}`}>{t.allows_virtual ? 'Virtual ✓' : 'Sin virtual'}</span>
                    <span className={`badge badge-${t.allows_invitados ? 'success' : 'neutral'}`}>{t.allows_invitados ? 'Admite invitados' : 'Sin invitados'}</span>
                  </div>
                </li>
              ))}
            </ul>
            {eventTypes.length === 0 && <p>Sin tipos de evento todavía.</p>}
          </div>
        </div>
      )}

      {attendanceDetailModal && (
        <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setAttendanceDetailModal(null); }}>
          <div className="modal-box detail-box" role="dialog" aria-modal="true">
            <div className="detail-header">
              <h3>Asistencia de {attendanceDetailModal.user.name}</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setAttendanceDetailModal(null)}>Cerrar</button>
            </div>
            {attendanceDetailModal.rows.length === 0 ? <p>Sin fechas pasadas todavía.</p> : (
              <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', fontSize: '13px' }}>
                {attendanceDetailModal.rows.map(r => (
                  <li key={r.event_id} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                    <span>
                      {EVENT_TYPE_LABELS[r.event_type] || r.event_type} — {formatEventDate(r.event_date)}{' '}
                      <StatusPill tone={r.status === 'presente' ? 'success' : 'danger'}>{r.status === 'presente' ? 'Presente' : 'Ausente'}</StatusPill>
                    </span>
                    {r.status === 'presente'
                      ? <button className="btn btn-secondary btn-sm" onClick={() => toggleAttendanceStatus(r.event_id, 'ausente')}>Corregir a ausente</button>
                      : <button className="btn btn-secondary btn-sm" onClick={() => toggleAttendanceStatus(r.event_id, 'presente')}>Corregir a presente</button>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

export default AdminUsers;
